<template>
    <Vueform v-show="!showMarkup" ref="form$" v-bind="model.form" :schema="model.schema" :locale="locale" :id="idPrefix"
        :disabled="formCompleted" v-on:success="success" v-on:error="error" :prepare="prepareForm"
        :displayErrors="displayErrors" :float-placeholders="floatPlaceholders" class="vfp-form">

    </Vueform>
    <div v-show="showMarkup" v-html="thankYouMarkup"></div>
</template>

<script>
import { ref, onMounted, computed, watch, watchEffect, nextTick } from 'vue'
import adsState from './utils/page-state-reader.js'
import { getRandomString } from './utils/random-string.js'
import { watchAdsCountryChangePlugin } from './plugins/country-change-form-plugin.js'
import formHelper from './utils/form-helper.js'

const genericErrorMessage = 'Unable to submit your form. Please try again later.';

const analyticsName = "vfp_ads_analytics";
const formLocaleName = "vfp_form_locale";
const contextName = 'vfp_context';
const pardotValuesName = "vfp_pardot_visitors";
const sitefinityUserIdName = "vfp_sf_user_id";

export default {
    props: ['model', 'locale'],
    name: 'DynamicForm', //used by code to verify we are talking to the right component
    setup(props, context) {

        const form$ = ref(null);
        const model = ref(props.model);

        const displayErrors = ref(true);

        //get locale from property
        const locale = ref(props.locale ?? 'en');

        //display mode if we should show the thank you markup
        const showMarkup = ref(false);
        const thankYouMarkup = ref('');

        //should we float placeholders
        const floatPlaceholders = ref(!(model.value.hideFloatingPlaceholders == "true"));

        //track form completion status so we can disable the form
        const formCompleted = ref(false);

        //a place to store the userid that was determined at submit time
        let currentUserId = null;


        //todo:consider getting this from a prop so that main.js can set it from the script block that creates us
        const idPrefix = `vfp-${getRandomString(8)}`;


        //multi step unique support
        let replacements = [];
        for (const schemaName in model.value.schema) {
            if (schemaName.startsWith('unique_container')) {
                replacements.push({ from: schemaName, to: idPrefix + `-${replacements.length + 1}` });
            }
        }

        //reparse model with replacements
        if (replacements.length > 0) {
            let jsonSchema = JSON.stringify(model.value);
            replacements.forEach(x => { jsonSchema = jsonSchema.replaceAll(x.from, x.to) });
            model.value = JSON.parse(jsonSchema);
        }

        if (model.value.context) {
            //if homeowner rule add homeowner to the role field
            const rules = model.value.context["Rules"];
            if (rules && rules == 'find-a-rep homeowner') {

            }
        }

        onMounted(() => {

            const form = form$.value;

            //this updates data without resetting existing
            if (model.value.defaults) {

                //if project_type is specified as a default, HIDE the element
                if (model.value.defaults["project_type"]) {
                    const projectTypeElName = form.flatTree.filter(x => x.endsWith('.project_type')).at(0);
                    if (form.el$(projectTypeElName)) {
                        console.log("VFP: Default for project_type and element found.");
                        form.el$(projectTypeElName).hide();
                    }
                }

                form.update(model.value.defaults);
            }

            if (model.value.context) {
                //if homeowner rule add homeowner to the role field
                const rules = model.value.context["Rules"];
                if (rules && rules == 'find-a-rep homeowner') {
                    const roleElementName = form.flatTree.filter(x => x.endsWith('.role')).at(0);
                    if (roleElementName) {
                        const roleElement = form.el$(roleElementName);
                        if (roleElement) {
                            const foundRole = formHelper.findChildSchemaAndPath(form, idPrefix, 'role');
                            if (foundRole && foundRole.schema) {
                                foundRole.schema.items.push({ "label": "Homeowner", "value": "Homeowner" });
                            }
                        }
                    }
                }
            }

            //watch the country value in the data and change the state/region labels accordingly
            watchAdsCountryChangePlugin(form$);




            //if we have steps, disable showing errors until we try to submit the form
            const configuredDisplayErrorValue = (model?.value?.form?.displayErrors ?? true) == true;
            if (form.hasSteps && configuredDisplayErrorValue) {
                displayErrors.value = false;
                form.steps$.on('select', (step) => {
                    if (step.isLast) {
                        displayErrors.value = true;
                    } else {
                        displayErrors.value = false;
                    }
                });
            } else {
                displayErrors.value = configuredDisplayErrorValue;
            }

            //example event subscription
            // form.on('submit',function(param){
            //     console.log(param);
            // })

            //report locale
            console.log(`VFP: locale: ${form.locale}`)

            //callback function
            if (model.value.callBackFn && typeof (window[model.value.callBackFn]) == "function") {
                window[model.value.callBackFn](form, model.value);
            }


        });


        function success(response) {
            const messages = form$.value.messageBag;

            if (response.data && response.data.success) {
                const data = response.data;

                //should we show a user message
                if (data.userMessage) {
                    messages.append(data.userMessage, 'message');
                }

                //the values were successfully captured by the server
                //should we show different markup
                if (data.thankYouMarkup) {
                    thankYouMarkup.value = data.thankYouMarkup;
                    showMarkup.value = true;
                }

                //have the form emit an event in the hopes that GA will pick it up
                form$.value.$el.dispatchEvent(new Event("submit"));

                //register GTM data layer event specified by the form model
                if (window.dataLayer?.push && model.value.gtmSuccessEvent) {
                    const gtmEvent = currentUserId ? { ...model.value.gtmSuccessEvent, "tool_user_id": currentUserId } : { ...model.value.gtmSuccessEvent };
                    console.log("VFP: datalayer push", gtmEvent);
                    dataLayer.push(gtmEvent);
                }

                //should we redirect as specified by the server's response
                if (data.redirectUrl) {
                    console.log("VFP: server specified redirect")
                    window.location.href = data.redirectUrl;
                }
                //should we redirect to a form data supplied thank you page?
                else if (model.value.thankYouUrl) {
                    console.log("VFP: thank you redirect")
                    window.location.href = model.value.thankYouUrl;
                }


                console.log("VFP: form submitted")
                formCompleted.value = true;

            } else {
                //something failed, but the request was successful

                const data = response.data ?? {};
                messages.clear();
                messages.append(data.userMessage ?? genericErrorMessage);
            }

        }

        function error(error, details) {
            const messages = form$.value.messageBag;
            messages.clear();
            messages.append(genericErrorMessage);
            console.log('VFP: Submit endpoint failed', error, details);
        }

        async function prepareForm(form) {
            //we can add information to the request data being submitted here

            //collapse $x values into non $ field
            //handles only single values and results in the value being the last one it finds
            Object.keys(form.requestData).forEach(key => {
                if (key.includes('$') && form.requestData[key]) {
                    const beforeDollar = key.split('$')[0];
                    if (beforeDollar) {
                        form.requestData[beforeDollar] = form.requestData[key];
                    }
                }
            });

            //analytics
            const analyticsValues = adsState.getAdsAnalyticsCookieObject();
            if (analyticsValues) {
                form.requestData[analyticsName] = JSON.stringify(analyticsValues);
            }

            //pardot ids
            const pardotVisitorValues = adsState.getPardotVisitorCookieValues();
            if (pardotVisitorValues) {
                form.requestData[pardotValuesName] = JSON.stringify(pardotVisitorValues);
            }

            //sitefinity user id
            const userId = await adsState.getCurrentUserId();
            if (userId) {
                form.requestData[sitefinityUserIdName] = userId;
                currentUserId = userId;
            }

            //locale
            form.requestData[formLocaleName] = locale.value;

            //context
            if (model.value.context) {
                form.requestData[contextName] = JSON.stringify(model.value.context);
            }
        }

        //example computed property
        // const schema = computed(()=>{
        //     return model.schema;
        // });



        return {
            model,
            locale,
            form$, //this must be returned otherwise its not available in onMounted
            idPrefix, //id prefix is looked for by the id-prefix-plugin, and it is apart of the id of the vueform
            success,
            error,
            showMarkup,
            thankYouMarkup,
            prepareForm,
            formCompleted,
            displayErrors,
            floatPlaceholders
        }
    }
}
</script>